import { UserRole } from '@/types/user';

export type LetterType =
  | 'welcome'
  | 'compliance'
  | 'schedule'
  | 'continued_schedule'
  | 'lost_to_follow_up';

export type LetterDeviceScope = 'inductive' | 'inductive_wireless';
export type LetterFrequencyMode = 'on_schedule_change' | 'cadence';

export interface LetterTemplate {
  letterType: LetterType;
  postgridTemplateId: string | null;
  enabled: boolean;
  deviceScope: LetterDeviceScope | null;
  frequencyMode: LetterFrequencyMode | null;
  cadenceDays: number | null;
}

export interface ClinicLetters {
  lettersEnabled: boolean;
  templates: LetterTemplate[];
}

export interface LetterClinic {
  id: string;
  name: string;
}

/** Rhythm-wide PostGrid templates. Editing one of these changes every clinic still pointed at it. */
export const SHARED_TEMPLATE_IDS = new Set([
  'template_fo6DXe8mjr3xsrXBHFWRU9',
  'template_rC4HRz1q6P1yEcLitCyi8E',
  'template_vDTYoKvdhzJtxBhXZ9fsJG',
  'template_mhdzVNatgSEf16bFCzRn1i'
]);

const TEMPLATE_ID = /^template_[A-Za-z0-9]+$/;

const LETTER_ADMIN_ROLES = new Set<string>([
  UserRole.ExternalAdmin,
  UserRole.ExternalPractitionerAdmin,
  UserRole.ExternalNonPractitionerAdmin
]);

/** Same letters the ops portal lists. Continued schedule and lost-to-follow-up are not mailed from here. */
export const LETTER_CATALOG: {
  letterType: LetterType;
  label: string;
  whenItSends: string;
}[] = [
  {
    letterType: 'welcome',
    label: 'Welcome Letter',
    whenItSends: 'When a patient is enrolled'
  },
  {
    letterType: 'compliance',
    label: 'Compliance Letter',
    whenItSends: 'When a patient is inactivated for a selected reason'
  },
  {
    letterType: 'schedule',
    label: 'Schedule Letter',
    whenItSends: 'Upcoming transmission schedule reminders'
  }
];

export const LETTER_SHORT_CODES: { code: string; description: string }[] = [
  { code: '{{firstName}}', description: "Patient's first name" },
  { code: '{{lastName}}', description: "Patient's last name" },
  { code: '{{practiceName}}', description: 'Clinic name' },
  { code: '{{doctorName}}', description: 'Following physician' },
  { code: '{{date}}', description: 'Date the letter is generated' },
  { code: '{{scheduleDate1}}', description: 'First upcoming transmission date' },
  { code: '{{scheduleDate2}}', description: 'Second upcoming transmission date' },
  { code: '{{scheduleDate3}}', description: 'Third upcoming transmission date' },
  { code: '{{scheduleDate4}}', description: 'Fourth upcoming transmission date' }
];

export const LETTER_UNIT_COST_USD = 0.89;

/** External clinic admins. There is no separate account-admin role. */
export function canManageLetterTemplates(role?: string | null): boolean {
  return Boolean(role && LETTER_ADMIN_ROLES.has(role));
}

/**
 * Clinics this person belongs to. Membership is clinic by clinic.
 * An account organization on the user is not a clinic and is left out.
 */
export function letterClinics(
  organizations?: { id?: string | null; name?: string | null; isAccount?: boolean | null }[] | null
): LetterClinic[] {
  const seen = new Set<string>();
  const clinics: LetterClinic[] = [];
  for (const org of organizations ?? []) {
    if (!org?.id || org.isAccount || seen.has(org.id)) continue;
    seen.add(org.id);
    clinics.push({ id: org.id, name: org.name?.trim() || 'Clinic' });
  }
  return clinics;
}

export function isTemplateId(value: unknown): value is string {
  return typeof value === 'string' && TEMPLATE_ID.test(value);
}

export function isSharedTemplate(templateId: string | null | undefined): boolean {
  return Boolean(templateId && SHARED_TEMPLATE_IDS.has(templateId));
}

function asLetterType(value: unknown): LetterType | null {
  return LETTER_CATALOG.some((row) => row.letterType === value) ? (value as LetterType) : null;
}

function asScope(value: unknown): LetterDeviceScope | null {
  return value === 'inductive' || value === 'inductive_wireless' ? value : null;
}

function asFrequency(value: unknown): LetterFrequencyMode | null {
  return value === 'on_schedule_change' || value === 'cadence' ? value : null;
}

export function lettersFromRecord(payload: unknown): ClinicLetters {
  const record =
    payload && typeof payload === 'object'
      ? (payload as { record?: { data?: unknown } }).record
      : undefined;
  const data =
    record?.data && typeof record.data === 'object'
      ? (record.data as Record<string, unknown>)
      : payload && typeof payload === 'object'
        ? (payload as Record<string, unknown>)
        : {};
  const rows = Array.isArray(data.templates) ? data.templates : [];
  const templates: LetterTemplate[] = [];
  for (const row of rows) {
    if (!row || typeof row !== 'object') continue;
    const source = row as Record<string, unknown>;
    const letterType = asLetterType(source.letterType);
    if (!letterType) continue;
    const templateId = source.postgridTemplateId ?? source.postGridTemplateId;
    const cadence = Number(source.cadenceDays);
    templates.push({
      letterType,
      postgridTemplateId: isTemplateId(templateId) ? templateId : null,
      enabled: source.enabled === true || source.isEnabled === true,
      deviceScope: asScope(source.deviceScope),
      frequencyMode: asFrequency(source.frequencyMode),
      cadenceDays: Number.isInteger(cadence) ? cadence : null
    });
  }
  return {
    lettersEnabled: data.lettersEnabled === true || data.isEnabled === true,
    templates
  };
}

/** Letters Rhythm has already given this clinic a template for. */
export function provisionedLetters(letters: ClinicLetters | null): LetterTemplate[] {
  if (!letters?.lettersEnabled) return [];
  const byType = new Map(letters.templates.map((row) => [row.letterType, row]));
  return LETTER_CATALOG.flatMap((meta) => {
    const row = byType.get(meta.letterType);
    return row?.postgridTemplateId ? [row] : [];
  });
}

export function letterMeta(letterType: LetterType) {
  return (
    LETTER_CATALOG.find((row) => row.letterType === letterType) ?? {
      letterType,
      label: letterType,
      whenItSends: ''
    }
  );
}
